import React, { forwardRef, useState, useImperativeHandle, useRef } from "react";
import { Box, Stack, Typography, IconButton, Divider } from "@mui/material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { Input } from "../Input";

import MailOutlineIcon from "@mui/icons-material/MailOutline";

const MultiplePerson = forwardRef((props, ref) => {
    const [persons, setPersons] = useState(
        props.values ? props.values : [{ firstName: "", lastName: "", email: "", contributesToRent: undefined }]
    );

    const firstNameRef = useRef();
    const lastNameRef = useRef();
    const emailRef = useRef();
    const phoneRef = useRef();
    const rentRef = useRef();
    const refs = [firstNameRef, lastNameRef, emailRef, phoneRef, rentRef];

    const checkboxOption = [
        { value: "Yes", label: "Yes" },
        { value: "No", label: "No" },
    ];

    useImperativeHandle(ref, () => ({
        validate,
    }));

    const validate = () => {
        let valid = false;
        for (let i = 0; i < refs.length; i++) {
            if (refs[i].current) {
                if (!refs[i].current.validate()) {
                    valid = true;
                }
            }
        }
        return !valid;
    };

    const handleAddClick = () => {
        let personRecords = [...persons, { firstName: "", lastName: "", email: "", contributesToRent: undefined }];
        setPersons(personRecords);
    };

    const handleRemoveClick = (index) => {
        let personRecords = [...persons];
        if (personRecords.length > 1) {
            personRecords.splice(index, 1);
            setPersons(personRecords);
            props.onChange(personRecords);
        }
    };

    const handleChange = (index, field, value) => {
        let personRecords = [...persons];
        personRecords[index][field] = value;
        setPersons(personRecords);

        props.onChange(personRecords);
    };

    const getExistingEmails = (index) => {
        let personRecords = [...persons.filter((item, i) => index !== i)];
        let emails = personRecords?.map((item) => item.email);
        if (props.existingEmails && props.existingEmails?.length > 0) {
            emails = [...emails, ...props.existingEmails];
        }
        return emails;
    };

    const getExistingPhones = (index) => {
        let personRecords = [...persons.filter((item, i) => index !== i && !item.phone)];
        let phones = personRecords?.map((item) => item.phone);
        if (props.existingPhones && props.existingPhones?.length > 0) {
            phones = [...phones, ...props.existingPhones];
        }
        return phones;
    };

    return (
        <Box>
            {persons.map((item, index) => (
                <Box
                    sx={{
                        p: 1,
                        borderRadius: "8px",
                        border: "1px solid #E6E8F0",
                        overflow: "visible",
                        background: "#f8fdff",
                        width: "100%",
                        mb: 2,
                    }}>
                    <Grid container spacing={2} justifyContent={"space-between"} alignItems={"center"}>
                        <Grid item>
                            <Typography gutterBottom variant="h5">
                                <b>
                                    {props.label} {index === 0 ? index + 1 : index + 1}
                                </b>
                            </Typography>
                        </Grid>
                        {(index > 1 || persons.length > 1) && (
                            <Grid item>
                                <IconButton aria-label="delete" size="small" style={{ float: "right" }}>
                                    <DeleteOutlinedIcon
                                        color="error"
                                        fontSize="small"
                                        onClick={() => handleRemoveClick(index)}
                                    />
                                </IconButton>
                            </Grid>
                        )}
                    </Grid>

                    <Divider sx={{ mt: 1, mb: 1 }} />

                    <Input
                        ref={firstNameRef}
                        id="firstName"
                        type="text"
                        value={item.firstName}
                        margin="normal"
                        label="First Name"
                        onChange={(value) => handleChange(index, "firstName", value)}
                        isRequired={true}></Input>

                    <Input
                        ref={lastNameRef}
                        id="lastName"
                        type="text"
                        label="Last Name"
                        value={item.lastName}
                        margin="normal"
                        onChange={(value) => handleChange(index, "lastName", value)}
                        isRequired={true}></Input>

                    <Input
                        ref={emailRef}
                        id="email"
                        type="email"
                        label="Email"
                        margin="normal"
                        value={item.email}
                        applicationId={props.applicationId}
                        verifyEmail={true}
                        prefix={<MailOutlineIcon></MailOutlineIcon>}
                        isRequired={true}
                        onChange={(value) => handleChange(index, "email", value)}
                        existingEmail={getExistingEmails(index)}></Input>
                    <Input
                        id="phone"
                        type="phone"
                        margin="normal"
                        ref={phoneRef}
                        value={item.phone}
                        applicationId={props.applicationId}
                        label={"Phone Number"}
                        autoComplete="off"
                        onChange={(value) => handleChange(index, "phone", value)}
                        mask={"(999) 999-9999"}
                        verifyPhone={true}
                        existingPhones={getExistingPhones(index)}
                        isRequired={true}></Input>
                    {props.displayRent && (
                        <>
                            <Input
                                type={"radio"}
                                ref={rentRef}
                                label="Will this adult be contributing to rent and participating in the Option to Own the home upon purchase?"
                                options={checkboxOption}
                                onChange={(value) =>
                                    handleChange(index, "contributesToRent", value === "Yes" ? true : false)
                                }
                                value={
                                    item.contributesToRent === undefined ? "" : item.contributesToRent ? "Yes" : "No"
                                }
                                isRequired={true}></Input>
                            <Typography variant="caption">
                                Any adult who is contributing to rent will have their income verified and added to the
                                underwriting process for approval. They will be a financially responsible part of the
                                lease and be an eligible part of the Option to Own agreement.
                            </Typography>
                        </>
                    )}
                </Box>
            ))}
            <Stack direction="row" spacing={2} sx={{ mt: 1, mb: 1 }} onClick={handleAddClick}>
                <AddCircleOutlineOutlinedIcon color="primary" />
                <Typography
                    variant="h6"
                    display="block"
                    gutterBottom
                    sx={{
                        textTransform: "none",
                        display: "flex",
                        alignItems: "center",
                        color: "#06aed4",
                        "&:hover": {
                            cursor: "pointer",
                        },
                    }}>
                    <b>Add another {props.label?.toLowerCase()}</b>
                </Typography>
            </Stack>
        </Box>
    );
});

export { MultiplePerson };
